import React, { useState, useMemo, useEffect } from "react";
import { TreeView, TreeItem } from "@mui/lab";
import Checkbox from "@mui/material/Checkbox";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

// 🔧 Configuration
const DISABLED_TYPES = ["Pays", "Ville"]; // niveaux non sélectionnables
const RETURN_TYPE = "Emplacement";

const EmplacementTree = ({ data, onChange, language = "fr", selectedIds = [] }) => {
  const [checked, setChecked] = useState(new Set());

  // 🔹 Initialiser checked à partir de selectedIds
  useEffect(() => {
    const initialChecked = new Set(selectedIds.map((id) => `${RETURN_TYPE}-${id}`));

    // 🔹 Cocher également les parents si tous leurs enfants sont cochés
    const updateParents = (nodes) => {
      nodes.forEach((node) => {
        if (node.children) {
          updateParents(node.children);
          const allChildrenChecked = node.children.every((child) => initialChecked.has(`${child.type}-${child.id}`));
          if (allChildrenChecked) {
            initialChecked.add(`${node.type}-${node.id}`);
          }
        }
      });
    };

    updateParents(data);
    setChecked(initialChecked);
  }, [selectedIds, data]);

  // 🔹 Déplier automatiquement le premier niveau
  const defaultExpanded = useMemo(
    () => data.map((node) => `${node.type}-${node.id}`),
    [data]
  );

  const getLabel = (node) => (language === "fr" ? node.labelFr : node.labelEn);
  const getKey = (node) => `${node.type}-${node.id}`;

  // 🔹 Récupère tous les descendants d’un nœud
  const getAllDescendants = (node) => {
    let result = [];
    if (node.children) {
      node.children.forEach((child) => {
        result.push(getKey(child));
        result = result.concat(getAllDescendants(child));
      });
    }
    return result;
  };

  const isChecked = (key) => checked.has(key);

  // 🔹 État indéterminé
  const isIndeterminate = (node) => {
    if (!node.children) return false;
    const descendants = getAllDescendants(node);
    const checkedCount = descendants.filter((k) => checked.has(k)).length;
    return checkedCount > 0 && checkedCount < descendants.length;
  };

  // 🔹 Toggle parent / enfants
  const handleToggle = (node) => {
    const key = getKey(node);
    const descendants = getAllDescendants(node);

    const newChecked = new Set(checked);
    const shouldCheck = !checked.has(key);

    if (shouldCheck) {
      newChecked.add(key);
      descendants.forEach((k) => newChecked.add(k));
    } else {
      newChecked.delete(key);
      descendants.forEach((k) => newChecked.delete(k));
    }

    // 🔹 Mettre à jour les parents
    const updateParents = (nodes, parent = null) => {
      nodes.forEach((n) => {
        if (n.children) {
          updateParents(n.children, n);
        }
        if (parent) {
          const allChildrenChecked = parent.children.every((child) =>
            newChecked.has(getKey(child))
          );
          if (allChildrenChecked) {
            newChecked.add(getKey(parent));
          } else {
            newChecked.delete(getKey(parent));
          }
        }
      });
    };
    updateParents(data);

    setChecked(newChecked);

    // 🔹 Retourner uniquement les Emplacements
    const selectedEmplacements = [...newChecked]
      .filter((k) => k.startsWith(`${RETURN_TYPE}-`))
      .map((k) => Number(k.split("-")[1]));

    onChange?.(selectedEmplacements);
  };

  // 🔹 Render des nœuds, isRoot = true pour le premier niveau
  const renderNode = (node, isRoot = false) => {
    const key = getKey(node);
    const disabled = !isRoot && DISABLED_TYPES.includes(node.type);

    return (
      <TreeItem
        key={key}
        nodeId={key}
        label={
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <Checkbox
              size="small"
              checked={isChecked(key)}
              indeterminate={isIndeterminate(node)}
              disabled={disabled}
              onChange={() => handleToggle(node)}
              onClick={(e) => e.stopPropagation()}
            />
            <span style={{ opacity: disabled ? 0.6 : 1 }}>{getLabel(node)}</span>
          </div>
        }
      >
        {node.children?.map((child) => renderNode(child))}
      </TreeItem>
    );
  };

  return (
    <TreeView
      multiSelect
      defaultExpanded={defaultExpanded}
      defaultExpandIcon={<ChevronRightIcon />}
      defaultCollapseIcon={<ExpandMoreIcon />}
    >
      {data.map((root) => renderNode(root, true))}
    </TreeView>
  );
};

export default EmplacementTree;
